import { TRACKS } from './music-data.ts';

/* The player.
 *
 * The music is written in rondocode (see audio/tracks.ts) and its note events
 * baked into music-data.ts; this file plays them. Nothing streams and nothing
 * loads — a track is a few hundred numbers and some oscillators.
 *
 * THE VOICES ARE NOT A CHIP ANY MORE (LS-205). Vijay: "the music needs a
 * substantial rewrite, chiptune doesn't make sense for this world anymore, we
 * need something between minecraft and zelda botw". The pulse and triangle
 * voices are still here for anything that wants them, and beside them: a
 * PIANO built from partials (each a sine with its own exponential decay, the
 * upper ones dying first, the way a struck string does, with a grain of
 * hammer noise on the attack), a LOWPASS on any pitched voice (a pad is two
 * detuned saws behind a lowpass; strings the same with a slow attack and a
 * vibrato), and an EXPONENTIAL envelope, because a linear decay is a ramp
 * and a piano's is a curve. test/mixdown.ts mirrors every one of them.
 *
 * Each track carries its own `patch` table: six instruments chosen for that
 * piece out of the catalogue in audio/tracks.mjs. The chip only ever had two
 * pulses, a triangle and a noise channel, but duty cycle, envelope, detune and
 * vibrato are enough to make the hollow's reedy 12.5% lead and the boss's
 * detuned, wobbling 25% snarl sound nothing like the overworld's square.
 *
 * Scheduling is the usual WebAudio two-clock arrangement: the game calls
 * update() every frame, and the player schedules whatever falls inside the
 * next fifth of a second onto the audio clock, which does not stutter when a
 * frame runs long.
 */

const LOOKAHEAD = 0.2;      // seconds of music queued in advance
const FADE = 0.35;          // crossfade when the area changes
/** How long a track's position stays worth resuming. Beyond about half a
 *  minute, coming back is a new arrival rather than a continuation — and a
 *  piece that resumes 20 bars in after a long absence just sounds like it
 *  started in the wrong place. */
const RESUME_WINDOW = 30;
/** Beats in a phrase: the score is written in four-bar questions and answers, and a fresh arrival begins on one. */
const PHRASE = 16;
/** Where a track is entered, in beats from its top: a track never heard starts at bar one; a return within the
 *  window resumes at the next BAR LINE after where it left (a resume on beat 2.5 is a skip); a return older than
 *  the window is a new arrival and begins on the next PHRASE line after where it left -- not bar one again, or a
 *  player who keeps stepping out for a minute hears the opening phrase every time and the later ones rarely (LS-233). */
/** The pieces that play once and stop: the hymn is the win, and a win that starts over every eighty-nine seconds is
 *  a loop, not an ending (the music lens, round 39). */
export const ONCE = new Set(['hymn']);
/** The event index after `i` of `len`: the top of the loop for a looping piece, -1 for a piece that has ended. */
export function advance(name, i, len) { return i + 1 < len ? i + 1 : ONCE.has(name) ? -1 : 0; }
export function entryBeat(back, now, bars, name = '') {
  /* A PIECE THAT PLAYS ONCE HAS NO PLACE TO COME BACK TO (2026-09-18). The resume rule is for the looping pieces --
   * walk away and the wilds carry on where you left them -- and the hymn is not one: it plays through and stops,
   * because it is the music of a run ending. Resuming it meant the SECOND win of a session opened wherever the
   * player happened to leave the first win screen, and then ended early, skipping up to half the piece. */
  if (name && ONCE.has(name)) return 0;
  if (!back) return 0;
  if (now - back.at < RESUME_WINDOW) return Math.ceil(back.beats / 4) * 4 % bars;
  return Math.ceil((back.beats + 1) / PHRASE) * PHRASE % bars;
}

/* THE SAME PIECE, TWO ROOMS (round 53). The hollow's piece became the wilds'
 * music after dark (LS-233) -- sparse and wet, which is right for a night --
 * but a track carries its room with it, and the hollow's room is a cave: 4.8
 * seconds of tail at half wet, damped to 0.7, with a 0.75-beat slapback off
 * close walls. Under an open sky that is a cave around a player standing in a
 * meadow, for about a third of every run.
 *
 * Only the walls change. The piece, its patches, its pans and its echo sends
 * are what they were; played outdoors it is heard in the wilds' own room. The
 * hollows themselves are untouched, which is the whole point of keeping the
 * decision here rather than editing the piece. */
const OPEN_AIR = {
  reverb: { seconds: 2.8, decay: 2.6, damp: 0.45, mix: 0.3 },
  echo: { beats: 1, feedback: 0.18, mix: 0.1, from: ['lead'] },
};

/** The room a track is played in, given where the player is standing. */
/** EVERY ROOM'S IMPULSE, MADE AHEAD (LS-188, 2026-09-11). `impulse` keeps
 *  each room's buffer once made, but the making -- seconds of seeded noise
 *  under a decay, lowpassed -- landed in the frame of the first switch into
 *  that room: 62 ms on a 4x phone as the country changed. Called behind the
 *  loading overlay, where a wait is honest, so no switch pays it. Returns the
 *  distinct rooms asked for. */
export function prewarmRooms(ac: any) {
  if (!ac) return 0;
  const seen = new Set<string>();
  const rooms: any[] = [];
  for (const t of Object.values(TRACKS) as any[]) { if (t && t.space) rooms.push(t.space, { ...t.space, ...OPEN_AIR }); }
  for (const sp of rooms) {
    const r = sp && sp.reverb; if (!r || !(r.mix > 0)) continue;
    const key = [r.seconds, r.decay, r.damp].join('|'); if (seen.has(key)) continue; seen.add(key);
    impulse(ac, r.seconds, r.decay, r.damp);
  }
  return seen.size;
}

export function roomFor(name: string | null, track: any, openAir: boolean) {
  const sp = track && track.space;
  if (!sp || !openAir || name !== 'hollow') return sp;
  return { ...sp, ...OPEN_AIR };
}

/** A reverb impulse: noise under an exponential decay, lowpassed as it goes so
 *  the tail darkens the way a real room's does. Generated, seeded and stereo —
 *  the two channels decorrelate, which is most of what makes a space sound
 *  wide rather than merely loud. No file, no download, about 20 ms to build. */
const IR_CACHE = new Map();
function impulse(ac, seconds, decay, damp) {
  // Context rate, because a ConvolverNode refuses a buffer at any other one.
  // Cached instead: there are five rooms in the game, each costs 20-40 ms to
  // build, and it is paid once, on the transition into the area that needs it
  // — a moment that is already a screen scroll.
  const rate = ac.sampleRate;
  const key = seconds + '/' + decay + '/' + damp + '/' + (rate | 0);
  const hit = IR_CACHE.get(key);
  if (hit) return hit;
  const n = Math.max(1, Math.floor(rate * seconds));
  const buf = ac.createBuffer(2, n, rate);
  let seed = 0x2545f491;
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    let lp = 0, energy = 0;
    const k = 1 - damp;
    for (let i = 0; i < n; i++) {
      seed ^= seed << 13; seed >>>= 0;
      seed ^= seed >>> 17;
      seed ^= seed << 5; seed >>>= 0;
      lp += k * ((seed / 2147483648) - 1 - lp);
      d[i] = lp * Math.pow(1 - i / n, decay);
      energy += d[i] * d[i];
    }
    // Normalise to unit energy, or `mix` does not mean what it says: a long
    // damped tail is intrinsically quiet, and the whole score came out five
    // decibels down the moment a room was put around it.
    const norm = energy > 0 ? 1 / Math.sqrt(energy) : 1;
    for (let i = 0; i < n; i++) d[i] *= norm;
  }
  IR_CACHE.set(key, buf);
  return buf;
}

/** A duty-cycle pulse as a Fourier series: harmonic k of a pulse of width d
 *  has amplitude 2/(k*pi) * sin(pi*k*d). A 50% duty zeroes every even
 *  harmonic and you get a square; 25% and 12.5% are the other two the NES
 *  could make, and they are why chiptune leads sound nasal rather than fat. */
function pulseWave(ac, duty, harmonics = 40) {
  const real = new Float32Array(harmonics + 1);
  const imag = new Float32Array(harmonics + 1);
  for (let k = 1; k <= harmonics; k++) real[k] = (2 / (k * Math.PI)) * Math.sin(Math.PI * k * duty);
  return ac.createPeriodicWave(real, imag, { disableNormalization: false });
}

/** THE SAMPLE BANKS (LS-205, the real instruments). rondocode's trained
 *  DDSP piano, viola, cello and bass, one note every few semitones, rendered
 *  offline (audio/samples.ts, tools/music-samples.sh) into public/music/.
 *  A patch that names a `sample` bank plays the nearest note of it, pitched
 *  by up to a whole tone, under the same envelope, lowpass and room the
 *  synth voice would have had; until the bank has arrived -- or where there
 *  is no fetch or no decoder -- the synth voice plays instead, so the music
 *  never waits on the network. */
export const nearestNote = (notes: number[], midi: number): number => {
  let best = notes[0];
  for (const n of notes) if (Math.abs(n - midi) < Math.abs(best - midi)) best = n;
  return best;
};
const BANKS: Map<string, { notes: number[]; buffers: Map<number, any>; ready: boolean; asked: boolean }> = new Map();
export function bankOf(name: string) { return BANKS.get(name); }
export function loadBank(ac, name: string, base = '/music/') {
  let bank = BANKS.get(name);
  if (bank) return bank;
  bank = { notes: [], buffers: new Map(), ready: false, asked: true };
  BANKS.set(name, bank);
  if (typeof fetch !== 'function' || !ac || typeof ac.decodeAudioData !== 'function') return bank;
  const dir = base + name + '/';
  fetch(dir + 'manifest.json').then((r) => (r.ok ? r.json() : null)).then((m) => {
    if (!m || !Array.isArray(m.notes)) return;
    const notes = m.notes.filter((n) => typeof n === 'number');
    let left = notes.length;
    for (const midi of notes) {
      fetch(dir + midi + '.mp3').then((r) => r.arrayBuffer()).then((ab) => ac.decodeAudioData(ab)).then((buf) => {
        bank.buffers.set(midi, buf);
        if (--left <= 0 || bank.buffers.size >= 3) { bank.notes = [...bank.buffers.keys()].sort((a, b) => a - b); bank.ready = true; }
      }).catch(() => { if (--left <= 0 && bank.buffers.size) { bank.notes = [...bank.buffers.keys()].sort((a, b) => a - b); bank.ready = true; } });
    }
  }).catch(() => { /* no bank: the synth voice plays */ });
  return bank;
}

export class Music {
  waves: any;
  ended: any;   // set when a piece runs out; assigned in a method, so the checker could not see it (2026-09-06)
  finished: any = null;   // the once-only piece that has run out, kept across a stop (2026-09-19): a music toggle on the win screen replayed the hymn from the top

  /* FIELDS, DECLARED.
   *
   * The migration's single biggest source of type errors — 4,646 of about
   * 5,300 — was this: properties assigned in methods rather than in the
   * constructor are invisible to the checker, so every read of one was an
   * error. Declaring them is what turns a class from a bag into a shape, and
   * a shape is what catches the defect that started this: `drawWorld`'s parts
   * object was missing `tiles` and nothing said so until it was reported from
   * play.
   *
   * They start as `any` because 44,000 lines cannot be typed in one step and
   * a wrong type is worse than a missing one. Narrowing them is the work that
   * follows, and it is worth doing in the order the bugs came from.
   */
  ac: any;
  /** Whether the piece is being heard under an open sky, which chooses its room. */
  openAir = false;
  dest: any;
  events: any;
  extra: any;
  extras: any;
  i: any;
  lfo: any[] | null = null;   // every oscillator's vibrato, one entry each
  loopStart: any;
  pulse: any = null; pulseLevel = 0.35;   // the kit's gain and its target (round 46)
  name: any;
  noise: any;
  out: any;
  pans: any;
  resumeBeat: any;
  saved: any;
  space: any;
  track: any;
  volume: any;


  constructor(ac, dest) {
    this.ac = ac;
    this.dest = dest;
    this.waves = new Map();
    this.name = null;
    this.openAir = false;   // whether the piece is being heard under an open sky
    this.track = null;
    this.out = null;
    this.events = [];
    this.i = 0;
    this.loopStart = 0;
    this.volume = 1;
    /* WHERE EACH TRACK WAS WHEN YOU LEFT IT. See `play`. */
    this.saved = new Map();
    this.noise = null;
  }

  wave(duty) {
    let w = this.waves.get(duty);
    if (!w) { w = pulseWave(this.ac, duty); this.waves.set(duty, w); }
    return w;
  }

  noiseBuffer() {
    if (this.noise) return this.noise;
    const ac = this.ac, n = Math.floor(ac.sampleRate * 0.5);
    const buf = ac.createBuffer(1, n, ac.sampleRate);
    const d = buf.getChannelData(0);
    // seeded, so the hats are the same grain every session and every machine
    let seed = 0x9e3779b9;
    for (let i = 0; i < n; i++) {
      seed ^= seed << 13; seed >>>= 0;
      seed ^= seed >>> 17;
      seed ^= seed << 5; seed >>>= 0;
      d[i] = (seed / 2147483648) - 1;
    }
    this.noise = buf;
    return buf;
  }

  /** Switch tracks. The outgoing one fades rather than cutting: walking into
   *  a hollow should feel like a door closing behind the overworld, and a
   *  hard splice in the middle of a bar just sounds like a bug. */
  /* A TRACK REMEMBERS WHERE IT WAS.
   *
   * `play` used to set `this.i = 0` and start the incoming piece at bar one,
   * every time. Stepping into a hollow and back out therefore restarted
   * `wilds` from the top — so a player who moves around hears bars 1-8 dozens
   * of times a session and bars 25-32, the only place the piece goes anywhere
   * new, close to never. Five loops the player only ever hears the front of.
   *
   * So the outgoing track writes down its position and the incoming one picks
   * it up, resuming at the next BAR LINE rather than mid-bar — a track that
   * resumes on beat 2.5 sounds like a skip, and the whole reason the fade
   * exists is that a hard splice sounds like a bug. Older than about half a
   * minute and it starts over, because by then returning is a new arrival
   * rather than a continuation.
   */
  play(name, openAir = false) {
    if (name === this.name && openAir === this.openAir) return;
    if (name && ONCE.has(name) && this.finished === name) return;   // a piece that plays once has played (2026-09-19)
    if (name !== this.finished) this.finished = null;   // ...until the score moves on to something else; the next win hears it again
    this.openAir = openAir;
    this.remember();
    this.fadeOut();
    this.name = name;
    this.ended = false;
    this.track = name ? TRACKS[name] : null;
    if (!this.track) return;
    this.events = [];
    for (const [voice, evs] of Object.entries(this.track.voices as Record<string, any>)) {
      for (const [t, d, n, v, pid] of evs) this.events.push([t, d, n, v, voice, pid]);
    }
    this.events.sort((a, b) => a[0] - b[0]);
    for (const p of [...(this.track.patches || []), ...Object.values(this.track.patch || {})] as any[]) if (p && p.sample) loadBank(this.ac, p.sample);
    this.i = 0;
    this.loopStart = 0;
    this.resumeBeat = 0;
    const back = this.saved.get(name);
    {
      const spb = 1 / (this.track.cps * 4);
      const bars = this.track.cycles * 4;
      const at = entryBeat(back, this.ac.currentTime, bars, name);
      /* Kept, not just used: this is the answer to "what will the player hear
       * from", and reconstructing it from `loopStart` afterwards means undoing
       * the 0.08s scheduling lead -- which is how a check ends up asserting
       * `beat 95.84 is a bar line`. */
      this.resumeBeat = at;
      let i = this.events.findIndex((e) => e[0] >= at);
      if (i < 0) i = 0;
      this.i = i;
      /* The loop is aligned to the BAR LINE, not to the first event after it.
       * Anchoring on the event instead makes the resume land wherever that
       * event happens to sit -- which is usually close, occasionally half a
       * bar out, and impossible to assert on. The bar line is the thing that
       * was chosen; put the clock there and let the events fall at their own
       * offsets from it. */
      this.loopStart = this.ac.currentTime + 0.08 - at * spb;
    }
    this.out = this.ac.createGain();
    this.out.gain.value = 0;
    this.out.connect(this.dest);
    this.buildSpace();
    const t0 = this.ac.currentTime;
    this.out.gain.setValueAtTime(0, t0);
    this.out.gain.linearRampToValueAtTime(this.volume * this.track.gain, t0 + FADE);
  }

  /** The room this track is played in: a convolution reverb and a tempo-synced
   *  echo, both fed by per-voice sends, plus a stereo position for each voice.
   *  All five pieces used to come out of the same dry mono point in the middle
   *  of the head, which is no way to tell an overworld from a cave. */
  buildSpace() {
    const ac = this.ac, sp = roomFor(this.name, this.track, this.openAir);
    this.space = null; this.pulse = null;   // the kit's pulse gain hangs off space.dry, so it dies with the space (2026-09-17: it survived a track change wired to the disposed output and the boss kit went silent); route() makes a new one at pulseLevel
    if (!sp) return;
    const dry = ac.createGain();
    dry.gain.value = 1;
    dry.connect(this.out);
    /** The room a track is played in. Declared whole — `reverb`, `echo` and
     *  `echoFrom` are attached further down, and a literal that does not
     *  mention them is a shape that forbids them. */
    const space: { dry: any; sends: Record<string, any>; reverb?: any; echo?: any; echoFrom?: any } =
      { dry, sends: {} };
    if (sp.reverb && sp.reverb.mix > 0) {
      const conv = ac.createConvolver();
      // normalize:false, because the node's own normalisation targets a fixed
      // loudness that has nothing to do with the mix I asked for — with it on,
      // putting a room around the score cost 3.5 to 4.8 dB of power and no
      // setting of `mix` could get it back. The impulse is already normalised
      // to unit energy above, which is the normalisation that makes `mix` mean
      // a wet fraction.
      conv.normalize = false;
      conv.buffer = impulse(ac, sp.reverb.seconds, sp.reverb.decay, sp.reverb.damp);
      const wet = ac.createGain();
      wet.gain.value = sp.reverb.mix;
      conv.connect(wet); wet.connect(this.out);
      // equal power: wet^2 + dry^2 = 1, so a room changes the sound and not
      // the level
      dry.gain.value = Math.sqrt(Math.max(0, 1 - sp.reverb.mix * sp.reverb.mix));
      space.reverb = conv;
    }
    if (sp.echo && sp.echo.mix > 0) {
      const spb = 1 / (this.track.cps * 4);
      const dl = ac.createDelay(2);
      dl.delayTime.value = Math.min(2, sp.echo.beats * spb);
      const fb = ac.createGain();
      fb.gain.value = sp.echo.feedback;
      // one lowpass in the loop, so repeats get darker instead of just quieter
      const tone = ac.createBiquadFilter();
      tone.type = 'lowpass';
      tone.frequency.value = 2600;
      const wet = ac.createGain();
      wet.gain.value = sp.echo.mix;
      dl.connect(tone); tone.connect(fb); fb.connect(dl);
      dl.connect(wet); wet.connect(this.out);
      if (space.reverb) wet.connect(space.reverb);
      space.echo = dl;
      space.echoFrom = new Set(sp.echo.from || []);
    }
    this.space = space;
    this.pans = new Map();
  }

  /** Where a voice sits, and what it feeds. One node per voice, reused. */
  route(name) {
    if (!this.space) return this.out;
    let node = this.pans.get(name);
    if (node) return node;
    const ac = this.ac, sp = this.track.space;
    const pan = (sp.pan && sp.pan[name]) || 0;
    node = ac.createStereoPanner ? ac.createStereoPanner() : ac.createGain();
    if (node.pan) node.pan.value = pan;
    // the kit rides a pulse gain: the beast's life sets it (the music lens, round 46)
    if (name === 'hat' || name === 'kick') { if (!this.pulse) { this.pulse = ac.createGain(); this.pulse.gain.value = this.pulseLevel || 0.35; this.pulse.connect(this.space.dry); } node.connect(this.pulse); }
    else node.connect(this.space.dry);
    // percussion goes to the room too, but less: a wet hat is a smear
    if (this.space.reverb) {
      const send = ac.createGain();
      send.gain.value = (name === 'hat' || name === 'kick') ? 0.35 : 1;
      node.connect(send); send.connect(this.space.reverb);
    }
    if (this.space.echo && this.space.echoFrom.has(name)) node.connect(this.space.echo);
    this.pans.set(name, node);
    return node;
  }

  /** The kit's weight, from the beast's life; ramped, so a blow is not a jump. */
  setPulse(v) { this.pulseLevel = v; if (this.pulse && this.ac) this.pulse.gain.setTargetAtTime(v, this.ac.currentTime, 0.4); }
  /** Write down how far into the loop the current track is, in beats, so
   *  `play` can pick it up if the player comes back. */
  remember() {
    if (!this.track || !this.name || !this.loopStart) return;
    if (ONCE.has(this.name)) return;   // and nothing is written down for it: a piece that has run out is not at a position (2026-09-18)
    const spb = 1 / (this.track.cps * 4);
    const bars = this.track.cycles * 4;
    const beats = ((this.ac.currentTime - this.loopStart) / spb) % bars;
    if (!Number.isFinite(beats) || beats < 0) return;
    this.saved.set(this.name, { beats, at: this.ac.currentTime });
  }

  fadeOut() {
    if (!this.out) return;
    const g = this.out, t0 = this.ac.currentTime;
    g.gain.cancelScheduledValues(t0);
    g.gain.setValueAtTime(g.gain.value, t0);
    g.gain.linearRampToValueAtTime(0, t0 + FADE);
    setTimeout(() => { try { g.disconnect(); } catch { /* already gone */ } }, (FADE + 0.5) * 1000);
    this.out = null;
    this.track = null;
    this.name = null;
  }

  /* ...AND MUTING IS LEAVING. `stop()` went straight to `fadeOut`, which
   * clears `this.name` before anything writes the position down — so mute and
   * unmute always restarted the track at bar one. Measured over four bot runs,
   * 34 of 36 track returns are death-and-respawn; this is the other way a
   * player leaves a track without changing area. */
  stop() { if (this.name && ONCE.has(this.name) && this.ended) this.finished = this.name; this.remember(); this.fadeOut(); }

  update() {
    const track = this.track;
    if (!track || !this.out) return;
    // A track with no events crashed at 60 Hz: this.events[this.i][0] on an
    // empty array, straight out of Game.update(), nine frames into a boss
    // fight. music-data.js is generated, and a half-written build is exactly
    // how that ships.
    if (!this.events.length || this.ended) return;
    const ac = this.ac, now = ac.currentTime;
    const spb = 1 / (track.cps * 4);            // one cycle is one bar of four
    const bars = track.cycles * 4;
    if (!this.loopStart) this.loopStart = now + 0.08;
    // A tab left in the background stops firing frames; the audio clock keeps
    // going. Without this the player wakes up owing thirty seconds of music
    // and schedules all of it at once.
    if (this.loopStart + this.events[this.i][0] * spb < now - 1) {
      /* ...AND IT RESUMES WHERE THE CLOCK IS, ON A BAR LINE (2026-09-19). This went back to bar one, so a stall of
       * a second -- a bake, a tab switch, a phone's pause -- restarted the piece, against the resume rule (LS-233). */
      const beat = (((now - this.loopStart) / spb) % bars + bars) % bars;
      let at = (Math.ceil(beat / 4) * 4) % bars;
      let i = this.events.findIndex((e) => e[0] >= at);
      if (i < 0) { i = 0; at = 0; }
      this.i = i;
      this.loopStart = now + 0.08 - at * spb;
    }
    let guard = 0;
    while (guard++ < 512) {
      const ev = this.events[this.i];
      const at = this.loopStart + ev[0] * spb;
      if (at > now + LOOKAHEAD) break;
      this.voice(ev[4], ev[2], Math.max(at, now), ev[1] * spb, ev[3], ev[5]);
      const next = advance(this.name, this.i, this.events.length);
      if (next < 0) { this.ended = true; break; }   // the piece is over: nothing more is scheduled until another is played
      if (next === 0) this.loopStart += bars * spb;
      this.i = next;
    }
  }

  /** A lowpass in front of a pitched voice's envelope (LS-205): `lp` is the
   *  cutoff in Hz, or a multiple of the note's pitch when `lpTrack` is set. */
  lowpass(v, freq) {
    const f = this.ac.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = Math.min(v.lpTrack ? freq * v.lp : v.lp, this.ac.sampleRate * 0.45);
    f.Q.value = v.lpq || 0.7;
    return f;
  }

  /** One oscillator for an instrument, with its vibrato already wired. */
  osc(v, freq, at) {
    const ac = this.ac;
    const o = ac.createOscillator();
    if (v.wave === 'pulse') o.setPeriodicWave(this.wave(v.duty));
    else o.type = v.shape || v.wave;   // `wave` may be a timbre's name; `shape` is the oscillator (LS-205)
    o.frequency.setValueAtTime(freq, at);
    if (v.vibrato) {
      // detune is in cents, which is exactly the unit a vibrato depth is
      // written in — no conversion, and it tracks the note's pitch for free
      const lfo = ac.createOscillator();
      lfo.frequency.value = v.vibrato.rate;
      const depth = ac.createGain();
      depth.gain.value = v.vibrato.depth;
      lfo.connect(depth); depth.connect(o.detune);
      (this.lfo || (this.lfo = [])).push({ osc: lfo, gain: depth });   // A LIST, NOT A SLOT (2026-09-17): a detune twin calls osc() twice, so the second call overwrote the first note's vibrato and left it unstarted and silent
    }
    return o;
  }

  /** A NOTE THAT OUTLIVES ITS TRACK (2026-09-18). The run-ending `stone` cue is struck on the score's own lead
   *  patch so the ending is in the score's voice -- and it is fired in the same breath as `mode = 'win'`, which
   *  eight frames later fades the CURRENT track out and disconnects the very gain those notes hang from. Measured:
   *  84% of the cue never sounded. `loose` routes a voice straight at the music bus instead of the track's output,
   *  so the four notes are heard whatever the jukebox does next. */
  strike(name, midi, at, dur, vel = 1, dest: any = undefined) { return this.voice(name, midi, at, dur, vel, 0, dest || true); }   // dest: a bus of the caller's (the effects bus for a cue that is a sound effect, 2026-09-19)
  voice(name, midi, at, dur, vel = 1, pid = 0, loose: any = false) {
    // The instrument is per NOTE, not per voice: a section that changes the
    // lead is a different patch on the same channel, which is how a cartridge
    // made a B section sound like somewhere else without spending a channel.
    const v = (this.track.patches && this.track.patches[pid])
      || (this.track.patch && this.track.patch[name]);
    if (!v) return;
    const ac = this.ac;
    const freq = 440 * Math.pow(2, (midi - 69) / 12);
    const g = ac.createGain();
    let src, tail;
    if (v.wave === 'noise') {
      src = ac.createBufferSource();
      src.buffer = this.noiseBuffer();
      src.loop = true;
      const f = ac.createBiquadFilter();
      // Highpassed, not lowpassed. The old drum ran every hit through a
      // lowpass at four times its written pitch, which threw away 10-14 dB of
      // a white-noise buffer and left the whole kit 26-32 dB under the mix —
      // deleting the entire voice changed the output by 0.01 dB.
      f.type = v.filter || 'lowpass';
      f.frequency.value = Math.min(v.cut || freq * 4 + 120, ac.sampleRate * 0.45);
      f.Q.value = v.q || 0.7;
      src.connect(f); f.connect(g);
      tail = v.d + v.r;
    } else if (v.sample && bankOf(v.sample) && bankOf(v.sample).ready) {
      /* THE REAL INSTRUMENT (LS-205): the bank's nearest note, pitched to
       * this one, under the note's own envelope. */
      const bank = bankOf(v.sample);
      const near = nearestNote(bank.notes, midi);
      const into = v.lp ? this.lowpass(v, freq) : g;
      if (into !== g) into.connect(g);
      src = ac.createBufferSource();
      src.buffer = bank.buffers.get(near);
      src.playbackRate.value = Math.pow(2, (midi - near) / 12);
      src.connect(into);
      tail = Math.max(dur, 0.02) + v.r;
    } else if (v.partials) {
      /* A STRUCK STRING (LS-205). One sine per partial -- [ratio, gain,
       * decay] -- each dying exponentially over its own share of the note's
       * decay, so the tone starts bright and ends as a fundamental. The
       * hammer is a few milliseconds of lowpassed noise. The partial gains
       * ride under the note's own envelope below. */
      const into = v.lp ? this.lowpass(v, freq) : g;
      if (into !== g) into.connect(g);
      src = null;
      const extras: any[] = [];
      for (const [ratio, gain, decay] of v.partials) {
        const o = ac.createOscillator();
        o.type = 'sine';
        o.frequency.setValueAtTime(freq * ratio, at);
        const pg = ac.createGain();
        pg.gain.setValueAtTime(gain, at);
        pg.gain.exponentialRampToValueAtTime(Math.max(gain * 1e-3, 1e-5), at + Math.max(0.02, v.d * decay));
        o.connect(pg); pg.connect(into);
        if (!src) src = o; else extras.push(o);
      }
      if (v.hammer) {
        const hs = ac.createBufferSource();
        hs.buffer = this.noiseBuffer();
        const hf = ac.createBiquadFilter();
        hf.type = 'lowpass'; hf.frequency.value = Math.min(4000, freq * 6);
        const hg = ac.createGain();
        hg.gain.setValueAtTime(v.hammer, at);
        hg.gain.exponentialRampToValueAtTime(1e-4, at + 0.012);
        hs.connect(hf); hf.connect(hg); hg.connect(into);
        hs.start(at); hs.stop(at + 0.03);
      }
      this.extras = extras;
      tail = Math.max(dur, 0.02) + v.r;
    } else {
      src = this.osc(v, freq, at);
      // the pad's darkness: a lowpass in front of the envelope (LS-205)
      const into = v.lp ? this.lowpass(v, freq) : g;
      if (into !== g) into.connect(g);
      // A second copy a few cents off, beating against the first. It is the
      // difference between the boss's bass and the overworld's, on the same
      // waveform.
      if (v.detune) {
        const twin = this.osc(v, freq, at);
        twin.detune.setValueAtTime(v.detune, at);
        const mix = ac.createGain();
        mix.gain.value = 0.5;
        src.connect(mix); twin.connect(mix); mix.connect(into);
        this.extra = twin;
      } else {
        src.connect(into);
      }
      // The NES kick is a triangle whose pitch falls off a cliff. Without the
      // sweep it is a bass note on the wrong beat.
      if (v.sweep) src.frequency.exponentialRampToValueAtTime(
        Math.max(20, freq * v.sweep), at + v.d);
      tail = Math.max(dur, 0.02) + v.r;
    }
    // A real ADSR, not a blip: the sustain is what makes a held bass note read
    // as one note instead of eight retriggers. Velocity is baked into the
    // event by audio/build.mjs — metric accent times the bar's dynamic — so a
    // downbeat is louder than the sixteenth after it and a bridge is quieter
    // than the chorus around it.
    const peak = v.gain * vel;
    const hold = Math.max(dur * 0.92, 0.03);
    g.gain.setValueAtTime(0, at);
    g.gain.linearRampToValueAtTime(peak, at + v.a);
    const sus = peak * v.s;
    if (v.exp) {
      /* THE CURVE, NOT THE RAMP (LS-205). A piano's decay and a pad's release
       * are exponential: to a thousandth of the peak over the decay, then the
       * release from wherever the note is let go. Mirrored in test/mixdown.ts. */
      if (v.s > 0) {
        const end = Math.max(hold, v.a + v.d);
        g.gain.exponentialRampToValueAtTime(Math.max(sus, 1e-4), at + v.a + v.d);
        g.gain.setValueAtTime(Math.max(sus, 1e-4), at + end);
        g.gain.exponentialRampToValueAtTime(1e-4, at + end + v.r);
        g.gain.setValueAtTime(0, at + end + v.r + 0.001);
        tail = end + v.r;
      } else {
        g.gain.exponentialRampToValueAtTime(Math.max(peak * 1e-3, 1e-4), at + v.a + v.d + v.r);
        g.gain.setValueAtTime(0, at + v.a + v.d + v.r + 0.001);
        tail = v.a + v.d + v.r;
      }
    } else if (v.s > 0) {
      g.gain.linearRampToValueAtTime(Math.max(sus, 1e-4), at + v.a + v.d);
      g.gain.setValueAtTime(Math.max(sus, 1e-4), at + Math.max(hold, v.a + v.d));
      g.gain.linearRampToValueAtTime(0, at + Math.max(hold, v.a + v.d) + v.r);
      tail = Math.max(hold, v.a + v.d) + v.r;
    } else {
      g.gain.linearRampToValueAtTime(0, at + v.a + v.d + v.r);
      tail = v.a + v.d + v.r;
    }
    g.connect(loose ? (loose === true ? this.dest : loose) : this.route(name));   // `loose`: straight at the music bus, so a track change cannot take the note with it (2026-09-18)
    src.start(at);
    src.stop(at + tail + 0.02);
    if (this.extras) {
      for (const o of this.extras) { o.start(at); o.stop(at + tail + 0.02); }
      this.extras = null;
    }
    if (this.extra) {
      const twin = this.extra;
      this.extra = null;
      twin.start(at);
      twin.stop(at + tail + 0.02);
    }
    if (this.lfo) {
      for (const { osc, gain } of this.lfo) {
        osc.start(at);
        osc.stop(at + tail + 0.02);
        osc.onended = () => { try { gain.disconnect(); } catch { /* already gone */ } };
      }
      this.lfo = null;
    }
    src.onended = () => { try { g.disconnect(); } catch { /* already gone */ } };
  }
}
